<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import type { JournalArticle } from '~/utils/solr/journal'
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import type { BookAndArticle } from '~/utils/solr/literature'
import { NuxtLink } from '#components'
import { faBuildingColumns, faCalendarDays, faPenNib, faScaleBalanced, faSection } from '@fortawesome/pro-light-svg-icons'
import JournalAuthorList from '~/components/reading/JournalAuthorList.vue'
import { useResolvedAuthorLinks } from '~/composables/author/useResolvedAuthorLinks'
import { formatPageSpan } from '~/utils/journals/page-reference'
import { extractLabel } from '#shared/utils/solr-facet'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  node: BookAndArticle | LawAndCommentary | JournalArticle
  orientation: 'horizontal' | 'vertical'
}>()

type OptionalString = string | undefined | null

type MetadataNode = {
  id: string
  icon: IconDefinition
  underlined?: boolean
} & ({
  label: OptionalString
} | {
  links: Array<{
    label: OptionalString
    url?: RouteLocationRaw
    education?: OptionalString
  }>
})

function getAuthorLinks(
  authors: Array<{ label: OptionalString, slug?: string, education?: OptionalString }> = [],
  options: { underlined?: boolean } = {},
): MetadataNode {
  return {
    id: 'authors',
    icon: faPenNib,
    underlined: options.underlined,
    links: authors.map(author => ({
      label: author.label,
      education: author.education,
      url: author.slug && {
        name: 'author',
        params: {
          slug: author.slug,
        },
      },
    })),
  }
}

function getPageRange(node: JournalArticle): string | null {
  const pages = formatPageSpan(node.firstPage, node.lastPage)
  return pages ? `Side ${pages}` : null
}

function isJournalNode(node: BookAndArticle | LawAndCommentary | JournalArticle): node is JournalArticle {
  return node.nodeType === 'journal_article' || node.nodeType === 'journal_editorial'
}

const isJournal = computed(() => isJournalNode(props.node))
const isVerticalLayout = computed(() => props.orientation === 'vertical')
const journalNode = computed(() => isJournalNode(props.node) ? props.node : null)

const { links: authorLinks } = useResolvedAuthorLinks(computed(() => {
  const node = props.node
  if (isJournalNode(node)) {
    return { author: node.author, author_list: node.author_list }
  }
  if (node.nodeType === 'academic_article' || node.nodeType === 'book') {
    return { author: node.author ?? node.authorName, author_list: node.author_list }
  }
  return {}
}))

// For legal_commentary, show "last updated" (lastUpdatedByEditor) instead of publication date
function getDisplayDate(node: BookAndArticle | LawAndCommentary | JournalArticle): string | null {
  if (node.nodeType === 'legal_commentary' && 'lastUpdatedByEditor' in node && node.lastUpdatedByEditor) {
    return Array.isArray(node.lastUpdatedByEditor) ? node.lastUpdatedByEditor[0] : node.lastUpdatedByEditor
  }
  return getSolrDocumentDate(node)
}

const metadata = computed<Array<MetadataNode>>(() => {
  const date = getDisplayDate(props.node)
  const publishedAt = {
    id: 'publishedAt',
    icon: faCalendarDays,
    label: `${isJournal.value ? 'Publisert' : 'Sist endret'} ${formatDate(date, { year: 'numeric', month: 'long', day: 'numeric' })}`,
  }

  if ('source' in props.node) {
    return [
      {
        id: 'id',
        icon: faSection,
        label: props.node.id.toLocaleUpperCase(),
        underlined: true,
      },
      {
        id: 'title',
        icon: faScaleBalanced,
        label: props.node.shortTitle,
      },
      {
        id: 'ministry',
        icon: faBuildingColumns,
        label: props.node.ministry,
      },
      publishedAt,
    ]
  }
  else if (isJournal.value) {
    return [
      getAuthorLinks(authorLinks.value),
      publishedAt,
    ]
  }
  else if (props.node.nodeType === 'academic_article' || props.node.nodeType === 'book') {
    const publishedDate = formatDate(date, { year: 'numeric' })

    if (props.node.nodeType === 'book') {
      publishedAt.label = props?.node.editionNumber ? `${props.node.editionNumber}. utgave ${publishedDate}` : publishedDate
    }
    return [
      getAuthorLinks(authorLinks.value, { underlined: true }),
      {
        id: 'legalAreas',
        icon: faBuildingColumns,
        label: props.node.legal_areas?.length
          ? props.node.legal_areas.map(extractLabel).join(', ')
          : '',

      },
      publishedAt,
    ]
  }

  return []
})

const journalPageRange = computed(() => journalNode.value ? getPageRange(journalNode.value) : null)
</script>

<template>
  <ul
    v-bind="$attrs"
    class="flex"
    :class="{
      'flex-col gap-3xs justify-start': isVerticalLayout,
      'flex-row gap-2xs flex-wrap items-center justify-start': !isVerticalLayout,
    }"
  >
    <template v-for="entry in metadata" :key="entry.id">
      <li v-if="('label' in entry && entry.label) || ('links' in entry && entry.links.length > 0)">
        <Chip variant="neutral" color="carbon" :icon-left="entry.icon" size="medium" class="text-primary pl-0" :class="{ 'underline pl-0': entry.underlined }">
          <template v-if="'label' in entry">
            {{ entry.label }}
          </template>
          <template v-else-if="isJournal && entry.id === 'authors'">
            <JournalAuthorList :links="entry.links" />
          </template>
          <div v-else class="flex flex-wrap gap-x-2xs dark:text-carbon-50">
            <template v-for="link in entry.links" :key="link.label">
              <component
                :is="link.url ? NuxtLink : 'span'"
                v-if="link.label"
                :to="link.url"
                class="text-xsmall after:content-[','] last:after:content-['']"
                :class="{
                  'underline': !!link.url,
                  'no-underline cursor-default': !link.url,
                }"
              >
                {{ link.label }}
              </component>
            </template>
          </div>
        </Chip>
      </li>
    </template>
  </ul>
  <p v-if="journalPageRange" class="text-caption text-carbon-600 mt-2xs">
    {{ journalPageRange }}
  </p>
</template>
